import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { tagKeys } from '@/shared/lib/query/queryKeys';
import type { OwnerEditHook } from '@/shared/types/editHooks';
import type { UpdateOwnerItem } from '@/shared/types/owner';

import type { TeamOwner, UserOwner } from '@/entities/owner';
import { buildNewOwners, buildOwnerOperations, patchOwners } from '@/entities/owner';
import { useTeamOwners, useUserOwners } from '@/entities/owner/model';
import type { TagCategoryItem } from '@/entities/tags/domain';

type UseTagOwnerEditProps = {
  tagCategoryId?: string;
  tagCategory?: string;
};

export const useTagOwnerEdit = ({ tagCategoryId, tagCategory }: UseTagOwnerEditProps): OwnerEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const tagData = tagCategory && queryClient.getQueryData<TagCategoryItem>(tagKeys.categoryDetail(tagCategory));
  const currentOwners = useMemo(() => {
    if (!tagData) return [];
    return tagData.owners;
  }, [tagData]);

  const { data: teamOwners = [], isLoading: isLoadingTeams } = useTeamOwners();
  const { data: userOwners = [], isLoading: isLoadingUsers } = useUserOwners();

  const updateOwnersMutation = useMutation({
    mutationFn: async ({
      selectedTeamId,
      selectedUserIds,
      teams,
      users,
      currentOwners,
    }: {
      selectedTeamId: string;
      selectedUserIds: string[];
      teams: TeamOwner[];
      users: UserOwner[];
      currentOwners: UpdateOwnerItem[];
    }) => {
      if (!tagCategoryId) {
        throw new Error(NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR);
      }

      const newOwners = buildNewOwners({
        selectedTeamId,
        selectedUserIds,
        teams,
        users,
      });
      const operations = buildOwnerOperations({
        newOwners,
        currentOwners,
      });
      await patchOwners({
        resourceId: tagCategoryId,
        apiPath: '/classifications',
        operations,
      });
    },
    onError: () => {
      notification.error({
        message: NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR,
        description: NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR_DESC,
        duration: 5,
        placement: 'bottomLeft',
      });
    },
  });

  const currentTeamId = useMemo(
    () => currentOwners.find((owner) => owner.type === 'team')?.id || null,
    [currentOwners]
  );

  const currentUserIds = useMemo(() => {
    const ids = currentOwners.filter((o) => o.type === 'user').map((o) => o.id);

    return Array.from(new Set(ids)).sort();
  }, [currentOwners]);

  useEffect(() => {
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (!tagCategory) return;

    const latestTagData = queryClient.getQueryData<TagCategoryItem>(tagKeys.categoryDetail(tagCategory));
    const latestOwners = latestTagData?.owners || [];

    if (latestOwners.length > 0) {
      const teamOwner = latestOwners.find((owner) => owner.type === 'team');
      const userOwnerIds = latestOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id);

      setSelectedTeamId(teamOwner?.id || null);
      setSelectedUserIds(userOwnerIds);
    }
  }, [queryClient, tagCategory]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleTeamSelect = useCallback((teamId: string) => {
    setSelectedTeamId((prev) => (prev === teamId ? null : teamId));
    setSelectedUserIds([]);
  }, []);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
    setSelectedTeamId(null);
  }, []);

  const handleUpdate = useCallback(() => {
    if (!tagCategory) return;

    const latestTagData = queryClient.getQueryData<TagCategoryItem>(tagKeys.categoryDetail(tagCategory));
    const latestOwners = latestTagData?.owners || [];

    updateOwnersMutation.mutate(
      {
        selectedTeamId: selectedTeamId ?? '',
        selectedUserIds,
        teams: teamOwners,
        users: userOwners,
        currentOwners: latestOwners,
      },
      {
        onSuccess: () => {
          if (tagCategory) {
            queryClient.invalidateQueries({ queryKey: tagKeys.categoryDetail(tagCategory) });
          }
          notification.success({
            message: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS,
            description: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS_DESC,
            duration: 2,
            placement: 'bottomLeft',
          });
          setIsOpen(false);
        },
      }
    );
  }, [selectedTeamId, selectedUserIds, teamOwners, userOwners, queryClient, tagCategory, updateOwnersMutation]);

  const hasChanges = useMemo(() => {
    const teamChanged = currentTeamId !== selectedTeamId;
    const usersChanged =
      currentUserIds.length !== selectedUserIds.length || !currentUserIds.every((id) => selectedUserIds.includes(id));

    return teamChanged || usersChanged;
  }, [currentTeamId, currentUserIds, selectedTeamId, selectedUserIds]);

  return {
    isOpen,
    activeTab,
    selectedTeamId,
    selectedUserIds,
    teamOwners,
    userOwners,
    currentOwners,
    isLoadingTeams,
    isLoadingUsers,
    isUpdating: updateOwnersMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    setActiveTab,
    handleTeamSelect,
    handleUserSelect,
    handleUpdate,
  };
};
